import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    IconButton,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    alpha,
    useTheme,
    Fade,
    Grow,
    Stack,
    Avatar,
    TextField,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    FileUploadOutlined as FileUploadIcon,
    CalendarTodayOutlined as CalendarTodayIcon,
    GridViewOutlined as GridViewIcon,
    FormatListBulleted as FormatListBulletedIcon,
    FilterList as FilterListIcon,
    MoreVert as MoreVertIcon,
    PhoneOutlined as PhoneIcon,
    EmailOutlined as EmailIcon,
    SchoolOutlined as SchoolIcon,
    Search as SearchIcon,
    AccountCircleOutlined as AccountIcon
} from '@mui/icons-material';

import { getStudents } from '../Apis/StudentApis';
import { CircularProgress } from '@mui/material';

const StudentCard = ({ data }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isTeacher = location.pathname.includes('/teacher');
    const isPlacement = location.pathname.includes('/placement');
    const basePath = isTeacher ? '/teacher' : isPlacement ? '/placement' : '/admin';

    const getStatusColor = (status) => {
        switch (status) {
            case 'Active': return { bg: alpha('#22C55E', 0.1), text: '#16A34A' };
            case 'On Training': return { bg: alpha('#3B82F6', 0.1), text: '#2563EB' };
            case 'Placed': return { bg: alpha('#8B5CF6', 0.1), text: '#7C3AED' };
            default: return { bg: alpha('#64748B', 0.1), text: '#475569' };
        }
    };

    const statusStyle = getStatusColor(data.status);

    return (
        <Grow in={true} timeout={500}>
            <Paper
                elevation={0}
                sx={{
                    p: 2.5,
                    borderRadius: 5,
                    border: '1px solid #E2E8F0',
                    bgcolor: 'white',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)',
                        borderColor: theme.palette.primary.main
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="caption" fontWeight="900" sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.08), px: 1.5, py: 0.5, borderRadius: 2 }}>
                        {data.id}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Chip
                            label={data.status?.toUpperCase() || 'ACTIVE'}
                            size="small"
                            sx={{
                                borderRadius: 1.5,
                                height: 24,
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.text,
                            }}
                        />
                        <IconButton size="small" sx={{ ml: 1 }}><MoreVertIcon fontSize="small" /></IconButton>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 3 }}>
                    <Avatar
                        src={data.photo}
                        sx={{ width: 80, height: 80, borderRadius: 4, mb: 2, border: '4px solid white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}
                    >
                        {data.studentName?.charAt(0)}
                    </Avatar>
                    <Typography variant="h6" fontWeight="1000" color="#1E293B" sx={{ lineHeight: 1.2, mb: 0.5 }}>{data.studentName}</Typography>
                    <Typography variant="caption" fontWeight="bold" color="#64748B">
                        {data.admissions?.[0]?.trade?.trade?.name || 'N/A'} • {data.admissions?.[0]?.batch?.batchName || 'N/A'}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3, borderStyle: 'dashed' }} />

                <Stack spacing={1.5} sx={{ mb: 'auto' }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SchoolIcon sx={{ fontSize: 16, color: '#94A3B8' }} />
                        <Typography variant="body2" fontWeight="bold" color="#475569">{data.college?.name || 'N/A'}</Typography>
                    </Stack>
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 3 }}>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small" sx={{ bgcolor: '#F8FAFC', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}><PhoneIcon sx={{ fontSize: 18 }} /></IconButton>
                        <IconButton size="small" sx={{ bgcolor: '#F8FAFC', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) } }}><EmailIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Stack>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={() => navigate(`${basePath}/students/detailed-student`, { state: { student: data } })}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            borderRadius: 3,
                            bgcolor: '#0F172A',
                            '&:hover': { bgcolor: '#1E293B' }
                        }}
                    >
                        Portfolio
                    </Button>
                </Stack>
            </Paper>
        </Grow>
    );
};

const StudentList = ({ data }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isTeacher = location.pathname.includes('/teacher');
    const isPlacement = location.pathname.includes('/placement');
    const basePath = isTeacher ? '/teacher' : isPlacement ? '/placement' : '/admin';

    return (
        <Fade in={true}>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 5, overflow: 'hidden' }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B', py: 2.5 }}>STUDENT ID</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>FULL NAME</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>TRADE / BATCH</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>LOCATION</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }}>STATUS</TableCell>
                            <TableCell sx={{ fontWeight: '1000', color: '#64748B' }} align="right">PROFILE</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row) => (
                            <TableRow key={row.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                                <TableCell sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{row.userId || row.id}</TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar src={row.photo} sx={{ width: 32, height: 32, borderRadius: 1.5 }}>
                                            {row.studentName?.charAt(0)}
                                        </Avatar>
                                        <Typography variant="body2" fontWeight="1000" color="#1E293B">{row.studentName}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell sx={{ color: '#475569', fontWeight: 'bold' }}>
                                    {row.admissions?.[0]?.trade?.trade?.name || 'N/A'} ({row.admissions?.[0]?.batch?.batchName || 'N/A'})
                                </TableCell>
                                <TableCell sx={{ color: '#475569', fontWeight: 'bold' }}>{row.college?.name || 'N/A'}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={row.status}
                                        size="small"
                                        sx={{
                                            borderRadius: 1.5,
                                            fontWeight: 'bold',
                                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            color: theme.palette.primary.main
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate(`${basePath}/students/detailed-student`, { state: { student: row } })}
                                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 'bold', px: 2 }}
                                    >
                                        Inspect
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Fade>
    );
};

const Students = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [view, setView] = useState('grid');
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    const isTeacher = location.pathname.includes('/teacher');
    const isPlacement = location.pathname.includes('/placement');
    const basePath = isTeacher ? '/teacher' : isPlacement ? '/placement' : '/admin';

    useEffect(() => {
        const fetchStudentsData = async () => {
            setLoading(true);
            try {
                const query = { limit: 100 }; // Fetch a decent chunk for the registry
                if (searchTerm) query.search = searchTerm;
                
                const res = await getStudents(query);
                if (res?.success) setStudents(res.data?.data || res.data || []);
            } catch (error) {
                console.error("Failed to fetch students:", error);
            } finally {
                setLoading(false);
            }
        };

        const timer = setTimeout(() => {
            fetchStudentsData();
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const filteredStudents = students; // Backend handles filtering

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, minHeight: '100vh', bgcolor: '#F8FAFC' }}>
            {/* Header section */}
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: 5, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { md: 'center' }, gap: 3 }}>
                    <Box>
                        <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Trainee Registry</Typography>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                            <AccountIcon sx={{ fontSize: 16, color: '#64748B' }} />
                            <Typography variant="body2" color="#64748B" fontWeight="600">{filteredStudents.length} Students Enrolled</Typography>
                        </Stack>
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<FileUploadIcon />}
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: '1000', color: '#475569', borderColor: '#E2E8F0', bgcolor: 'white' }}
                        >
                            Bulk Import
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => navigate(`${basePath}/students/add-student-details`)}
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: '1000', bgcolor: '#0F172A', '&:hover': { bgcolor: '#1E293B' }, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                        >
                            Enroll Student
                        </Button>
                    </Stack>
                </Box>
            </Fade>

            {/* Filtering Controls */}
            <Grow in={true} timeout={800}>
                <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 5, border: '1px solid #E2E8F0', bgcolor: 'white', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <TextField
                        placeholder="Search by name or student ID..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8' }} /></InputAdornment>,
                        }}
                        sx={{ minWidth: { xs: '100%', lg: 400 }, '& .MuiOutlinedInput-root': { borderRadius: 4, bgcolor: '#F8FAFC' } }}
                    />

                    <Stack direction="row" spacing={2} sx={{ width: { xs: '100%', lg: 'auto' }, justifyContent: 'flex-end' }}>
                        <Button
                            startIcon={<CalendarTodayIcon />}
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', color: '#1E293B', borderColor: '#E2E8F0', height: 40 }}
                        >
                            Academic Year 2025
                        </Button>
                        <Box sx={{ display: 'flex', bgcolor: '#F1F5F9', p: 0.5, borderRadius: 3 }}>
                            <IconButton
                                size="small"
                                onClick={() => setView('grid')}
                                sx={{ borderRadius: 2, bgcolor: view === 'grid' ? 'white' : 'transparent', color: view === 'grid' ? theme.palette.primary.main : '#64748B', boxShadow: view === 'grid' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                            >
                                <GridViewIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setView('list')}
                                sx={{ borderRadius: 2, bgcolor: view === 'list' ? 'white' : 'transparent', color: view === 'list' ? theme.palette.primary.main : '#64748B', boxShadow: view === 'list' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
                            >
                                <FormatListBulletedIcon fontSize="small" />
                            </IconButton>
                        </Box>
                        <Button
                            startIcon={<FilterListIcon />}
                            variant="outlined"
                            size="small"
                            sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 'bold', color: '#1E293B', borderColor: '#E2E8F0', height: 40 }}
                        >
                            Segment Filters
                        </Button>
                    </Stack>
                </Paper>
            </Grow>

            {/* Results section */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 15 }}>
                    <CircularProgress size={40} thickness={5} />
                </Box>
            ) : filteredStudents.length > 0 ? (
                view === 'grid' ? (
                    <Grid container spacing={3}>
                        {filteredStudents.map((item, index) => (
                            <Grid item size={{ xs: 12, sm: 6, lg: 4, xl: 3 }} key={item.id}>
                                <StudentCard data={item} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <StudentList data={filteredStudents} />
                )
            ) : (
                <Stack spacing={2} alignItems="center" sx={{ py: 15 }}>
                    <Box sx={{ bgcolor: '#F1F5F9', p: 3, borderRadius: '50%' }}><SchoolIcon sx={{ fontSize: 64, color: '#94A3B8' }} /></Box>
                    <Typography variant="h6" fontWeight="1000" color="#475569">No Students Found</Typography>
                    <Typography variant="body2" color="#64748B">No records match your current search criteria.</Typography>
                </Stack>
            )}
        </Box>
    );
};

export default Students;
